import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Music, ArrowLeft, ArrowRight, User, BookOpen, CreditCard,
  CheckCircle, Phone, Mail, Calendar, MapPin, Users, ChevronRight,
  Shield, Check
} from 'lucide-react';
import {
  produits, getCoursProduits, getInstrumentsForCours, getProfesseur,
  formatMontant, getProduit
} from '../data/mockData';

const STEPS = [
  { label: 'Informations personnelles', icon: User },
  { label: 'Choix des cours', icon: BookOpen },
  { label: 'Instruments', icon: Music },
  { label: 'Récapitulatif & Validation', icon: CreditCard },
];

const InscriptionPublique = () => {
  const [currentStep, setCurrentStep] = useState(0);
  const [submitted, setSubmitted] = useState(false);

  const [eleve, setEleve] = useState({
    nom: '', prenom: '', dateNaissance: '', adresse: '',
  });
  const [responsable, setResponsable] = useState({
    nom: '', prenom: '', lien: 'parent', telephone: '', email: '',
  });
  const [selectedCoursIds, setSelectedCoursIds] = useState([]);
  const [selectedInstrumentIds, setSelectedInstrumentIds] = useState([]);
  const [paiementEcheances, setPaiementEcheances] = useState(1);
  const [rgpdConsent, setRgpdConsent] = useState(false);

  const handleEleveChange = (field, value) => {
    setEleve((prev) => ({ ...prev, [field]: value }));
  };

  const handleResponsableChange = (field, value) => {
    setResponsable((prev) => ({ ...prev, [field]: value }));
  };

  const toggleCours = (coursId) => {
    setSelectedCoursIds((prev) => {
      const next = prev.includes(coursId) ? prev.filter((id) => id !== coursId) : [...prev, coursId];
      const validInstruments = selectedInstrumentIds.filter(instId => {
        const inst = getProduit(instId);
        if (!inst || !inst.coursAssocies || inst.coursAssocies.length === 0) return true;
        return inst.coursAssocies.some(cid => next.includes(cid));
      });
      setSelectedInstrumentIds(validInstruments);
      return next;
    });
  };

  const toggleInstrument = (instrumentId) => {
    setSelectedInstrumentIds((prev) =>
      prev.includes(instrumentId) ? prev.filter((id) => id !== instrumentId) : [...prev, instrumentId]
    );
  };

  const adhesion = produits.find(p => p.obligatoire);
  const adhesionTarif = adhesion ? adhesion.tarif : 25;

  const availableInstruments = produits.filter(p => {
    if (p.categorie !== 'instrument') return false;
    if (!p.coursAssocies || p.coursAssocies.length === 0) return true;
    return p.coursAssocies.some(cid => selectedCoursIds.includes(cid));
  });

  const calculateTotal = () => {
    let total = adhesionTarif;
    selectedCoursIds.forEach((coursId) => {
      const c = getProduit(coursId);
      if (c) total += c.tarif;
    });
    selectedInstrumentIds.forEach((instId) => {
      const inst = getProduit(instId);
      if (inst) total += inst.tarif;
    });
    return total;
  };

  const canProceed = () => {
    if (currentStep === 0) {
      return (
        eleve.nom && eleve.prenom && eleve.dateNaissance &&
        responsable.nom && responsable.prenom && responsable.telephone && responsable.email
      );
    }
    if (currentStep === 1) return selectedCoursIds.length > 0;
    if (currentStep === 3) return rgpdConsent;
    return true;
  };

  const handleSubmit = () => {
    if (!rgpdConsent) return;
    setSubmitted(true);
  };

  if (submitted) {
    return (
      <div className="page-container" style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div className="form-card" style={{ maxWidth: 560, textAlign: 'center', padding: '3rem 2rem' }}>
          <div style={{
            width: 80, height: 80, borderRadius: '50%',
            background: 'linear-gradient(135deg, #22c55e, #16a34a)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            margin: '0 auto 1.5rem', color: '#fff'
          }}>
            <CheckCircle size={40} />
          </div>
          <h2 style={{ fontSize: '1.75rem', color: '#1a1a2e', marginBottom: '0.75rem' }}>
            Inscription envoyée !
          </h2>
          <p style={{ color: '#6b7280', lineHeight: 1.7, marginBottom: '0.5rem' }}>
            Merci pour votre inscription. Votre demande a bien été enregistrée et sera traitée
            par notre équipe dans les meilleurs délais.
          </p>
          <p style={{ color: '#6b7280', lineHeight: 1.7, marginBottom: '2rem' }}>
            Un email de confirmation a été envoyé à <strong>{responsable.email}</strong>.
          </p>
          <Link to="/login" className="btn btn-primary" style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
            <ArrowLeft size={18} />
            Retour à la page de connexion
          </Link>
        </div>
      </div>
    );
  }

  const renderStep1 = () => (
    <>
      <div className="form-section">
        <h3 style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: '1rem' }}>
          <User size={20} style={{ color: '#7c3aed' }} />
          Informations de l'élève
        </h3>
        <div className="form-row">
          <div className="form-group">
            <label>Nom <span style={{ color: '#ef4444' }}>*</span></label>
            <input type="text" value={eleve.nom} onChange={(e) => handleEleveChange('nom', e.target.value)} placeholder="Nom de famille" />
          </div>
          <div className="form-group">
            <label>Prénom <span style={{ color: '#ef4444' }}>*</span></label>
            <input type="text" value={eleve.prenom} onChange={(e) => handleEleveChange('prenom', e.target.value)} placeholder="Prénom" />
          </div>
        </div>
        <div className="form-row">
          <div className="form-group">
            <label><Calendar size={14} style={{ marginRight: 4, verticalAlign: 'middle' }} /> Date de naissance <span style={{ color: '#ef4444' }}>*</span></label>
            <input type="date" value={eleve.dateNaissance} onChange={(e) => handleEleveChange('dateNaissance', e.target.value)} />
          </div>
          <div className="form-group">
            <label><MapPin size={14} style={{ marginRight: 4, verticalAlign: 'middle' }} /> Adresse</label>
            <input type="text" value={eleve.adresse} onChange={(e) => handleEleveChange('adresse', e.target.value)} placeholder="Adresse complète" />
          </div>
        </div>
      </div>
      <div className="form-section" style={{ marginTop: '2rem' }}>
        <h3 style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: '1rem' }}>
          <Users size={20} style={{ color: '#7c3aed' }} />
          Responsable légal
        </h3>
        <div className="form-row">
          <div className="form-group">
            <label>Nom <span style={{ color: '#ef4444' }}>*</span></label>
            <input type="text" value={responsable.nom} onChange={(e) => handleResponsableChange('nom', e.target.value)} placeholder="Nom" />
          </div>
          <div className="form-group">
            <label>Prénom <span style={{ color: '#ef4444' }}>*</span></label>
            <input type="text" value={responsable.prenom} onChange={(e) => handleResponsableChange('prenom', e.target.value)} placeholder="Prénom" />
          </div>
        </div>
        <div className="form-group">
          <label>Lien avec l'élève</label>
          <select value={responsable.lien} onChange={(e) => handleResponsableChange('lien', e.target.value)}>
            <option value="parent">Parent</option>
            <option value="Mère">Mère</option>
            <option value="Père">Père</option>
            <option value="tuteur">Tuteur légal</option>
            <option value="autre">Autre</option>
          </select>
        </div>
        <div className="form-row">
          <div className="form-group">
            <label><Phone size={14} style={{ marginRight: 4, verticalAlign: 'middle' }} /> Téléphone <span style={{ color: '#ef4444' }}>*</span></label>
            <input type="tel" value={responsable.telephone} onChange={(e) => handleResponsableChange('telephone', e.target.value)} placeholder="06 12 34 56 78" />
          </div>
          <div className="form-group">
            <label><Mail size={14} style={{ marginRight: 4, verticalAlign: 'middle' }} /> Email <span style={{ color: '#ef4444' }}>*</span></label>
            <input type="email" value={responsable.email} onChange={(e) => handleResponsableChange('email', e.target.value)} placeholder="email@exemple.fr" />
          </div>
        </div>
      </div>
    </>
  );

  const renderStep2 = () => {
    const coursList = getCoursProduits();
    return (
      <div className="form-section">
        <h3 style={{ marginBottom: '0.5rem' }}>Sélectionnez vos cours</h3>
        <p style={{ color: '#6b7280', fontSize: '0.9rem', marginBottom: '1.5rem' }}>
          Choisissez un ou plusieurs cours de musique pour l'année scolaire.
        </p>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '0.75rem' }}>
          {coursList.map((c) => {
            const isSelected = selectedCoursIds.includes(c.id);
            const prof = getProfesseur(c.professeurId);
            return (
              <div
                key={c.id}
                className={`cours-card ${isSelected ? 'selected' : ''}`}
                onClick={() => toggleCours(c.id)}
                style={{ cursor: 'pointer', position: 'relative' }}
              >
                {isSelected && (
                  <div style={{
                    position: 'absolute', top: 8, right: 8,
                    background: '#7c3aed', borderRadius: '50%',
                    width: 24, height: 24, display: 'flex',
                    alignItems: 'center', justifyContent: 'center'
                  }}>
                    <Check size={14} color="#fff" />
                  </div>
                )}
                <div className="cours-card-content">
                  <div className="cours-nom">{c.nom}</div>
                  <div className="cours-details">
                    {prof && <span>{prof.prenom} {prof.nom}</span>}
                    <span> — {c.frequence?.join(', ')}</span>
                    <span> · {c.duree} min</span>
                  </div>
                  <div className="cours-tarif">{formatMontant(c.tarif)} / an</div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const renderStep3 = () => (
    <div className="form-section">
      <h3 style={{ marginBottom: '0.5rem' }}>Instruments</h3>
      <p style={{ color: '#6b7280', fontSize: '0.9rem', marginBottom: '1.5rem' }}>
        Optionnel — Sélectionnez les instruments souhaités pour les cours choisis.
      </p>
      {availableInstruments.length === 0 ? (
        <div style={{
          textAlign: 'center', padding: '2rem', color: '#9ca3af',
          background: '#f9fafb', borderRadius: 12
        }}>
          Aucun instrument disponible pour les cours sélectionnés.
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '0.75rem' }}>
          {availableInstruments.map((inst) => {
            const isSelected = selectedInstrumentIds.includes(inst.id);
            const coursLies = inst.coursAssocies
              ? inst.coursAssocies.map(cid => getProduit(cid)).filter(Boolean)
              : [];
            return (
              <div
                key={inst.id}
                className={`cours-card ${isSelected ? 'selected' : ''}`}
                onClick={() => toggleInstrument(inst.id)}
                style={{ cursor: 'pointer', position: 'relative' }}
              >
                {isSelected && (
                  <div style={{
                    position: 'absolute', top: 8, right: 8,
                    background: '#7c3aed', borderRadius: '50%',
                    width: 24, height: 24, display: 'flex',
                    alignItems: 'center', justifyContent: 'center'
                  }}>
                    <Check size={14} color="#fff" />
                  </div>
                )}
                <div className="cours-card-content">
                  <div className="cours-nom">{inst.nom}</div>
                  <div className="cours-details">
                    {inst.description}
                    {coursLies.length > 0 && (
                      <span> · Pour : {coursLies.map(c => c.nom).join(', ')}</span>
                    )}
                  </div>
                  <div className="cours-tarif">{formatMontant(inst.tarif)} / an</div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );

  const renderStep4 = () => {
    const total = calculateTotal();
    return (
      <div className="form-section">
        <h3 style={{ marginBottom: '1rem' }}>Récapitulatif de l'inscription</h3>
        <div className="recap-inscription" style={{ marginBottom: '1.5rem' }}>
          <h4 style={{ fontSize: '0.9rem', fontWeight: 600, marginBottom: '0.75rem', color: '#374151' }}>
            Élève : {eleve.prenom} {eleve.nom}
          </h4>
          <table className="recap-table-mini">
            <thead>
              <tr>
                <th style={{ textAlign: 'left', padding: '0.5rem 0', borderBottom: '2px solid #e5e7eb', color: '#6b7280', fontSize: '0.8rem', textTransform: 'uppercase' }}>Désignation</th>
                <th style={{ textAlign: 'right', padding: '0.5rem 0', borderBottom: '2px solid #e5e7eb', color: '#6b7280', fontSize: '0.8rem', textTransform: 'uppercase' }}>Montant</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td style={{ padding: '0.5rem 0', borderBottom: '1px solid #f3f4f6' }}>
                  {adhesion ? adhesion.nom : 'Adhésion annuelle'}
                </td>
                <td style={{ padding: '0.5rem 0', borderBottom: '1px solid #f3f4f6', textAlign: 'right', fontWeight: 500 }}>
                  {formatMontant(adhesionTarif)}
                </td>
              </tr>
              {selectedCoursIds.map((cid) => {
                const c = getProduit(cid);
                if (!c) return null;
                return (
                  <tr key={c.id}>
                    <td style={{ padding: '0.5rem 0', borderBottom: '1px solid #f3f4f6' }}>{c.nom}</td>
                    <td style={{ padding: '0.5rem 0', borderBottom: '1px solid #f3f4f6', textAlign: 'right', fontWeight: 500 }}>{formatMontant(c.tarif)}</td>
                  </tr>
                );
              })}
              {selectedInstrumentIds.map((instId) => {
                const inst = getProduit(instId);
                if (!inst) return null;
                return (
                  <tr key={inst.id}>
                    <td style={{ padding: '0.5rem 0', borderBottom: '1px solid #f3f4f6' }}>{inst.nom}</td>
                    <td style={{ padding: '0.5rem 0', borderBottom: '1px solid #f3f4f6', textAlign: 'right', fontWeight: 500 }}>{formatMontant(inst.tarif)}</td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr>
                <td style={{ padding: '0.75rem 0', fontWeight: 700, fontSize: '1.05rem', borderTop: '2px solid #e5e7eb' }}>Total annuel</td>
                <td style={{ padding: '0.75rem 0', fontWeight: 700, fontSize: '1.05rem', borderTop: '2px solid #e5e7eb', textAlign: 'right', color: '#7c3aed' }}>{formatMontant(total)}</td>
              </tr>
            </tfoot>
          </table>
        </div>

        <div className="form-section" style={{ marginTop: '1.5rem' }}>
          <h4 style={{ marginBottom: '0.75rem', fontSize: '0.95rem', fontWeight: 600 }}>Échéancier de paiement</h4>
          <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
            {[1, 2, 3, 4].map((n) => (
              <button
                key={n}
                type="button"
                onClick={() => setPaiementEcheances(n)}
                style={{
                  flex: 1, minWidth: 80, padding: '0.75rem',
                  borderRadius: 10, border: '2px solid',
                  borderColor: paiementEcheances === n ? '#7c3aed' : '#e5e7eb',
                  background: paiementEcheances === n ? '#f5f3ff' : '#fff',
                  color: paiementEcheances === n ? '#7c3aed' : '#374151',
                  fontWeight: 600, cursor: 'pointer', textAlign: 'center',
                }}
              >
                <div style={{ fontSize: '1.1rem' }}>{n}×</div>
                <div style={{ fontSize: '0.8rem', fontWeight: 400, color: '#9ca3af', marginTop: 2 }}>
                  {formatMontant(Math.ceil(total / n))}
                </div>
              </button>
            ))}
          </div>
        </div>

        <div style={{
          marginTop: '2rem', padding: '1.25rem', borderRadius: 12,
          background: '#f8fafc', border: '1px solid #e2e8f0'
        }}>
          <label style={{ display: 'flex', gap: 12, cursor: 'pointer', alignItems: 'flex-start' }}>
            <input
              type="checkbox"
              checked={rgpdConsent}
              onChange={(e) => setRgpdConsent(e.target.checked)}
              style={{ marginTop: 4, width: 18, height: 18, accentColor: '#7c3aed' }}
            />
            <div style={{ fontSize: '0.85rem', color: '#4b5563', lineHeight: 1.6 }}>
              <Shield size={14} style={{ verticalAlign: 'middle', marginRight: 4, color: '#7c3aed' }} />
              <strong>Protection des données (RGPD)</strong><br />
              J'accepte que les données personnelles saisies dans ce formulaire soient collectées et traitées
              par l'école de musique Harmonie dans le cadre de la gestion des inscriptions, conformément au
              Règlement Général sur la Protection des Données (RGPD) européen.
            </div>
          </label>
        </div>
      </div>
    );
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 0: return renderStep1();
      case 1: return renderStep2();
      case 2: return renderStep3();
      case 3: return renderStep4();
      default: return null;
    }
  };

  return (
    <>
      <style>{`
        .inscription-header {
          background: linear-gradient(135deg, #6c5ce7, #a855f7);
          color: #fff;
          padding: 1.25rem 2rem;
          display: flex;
          align-items: center;
          justify-content: space-between;
        }
        .inscription-header-brand {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          font-size: 1.25rem;
          font-weight: 700;
        }
        .inscription-header a {
          color: rgba(255,255,255,0.85);
          text-decoration: none;
          font-size: 0.9rem;
          display: flex;
          align-items: center;
          gap: 6px;
        }
        .inscription-header a:hover { color: #fff; }
        .inscription-body {
          max-width: 860px;
          margin: 0 auto;
          padding: 2rem 1.5rem 4rem;
        }
        .inscription-title {
          text-align: center;
          margin-bottom: 2rem;
        }
        .inscription-title h1 {
          font-size: 1.75rem;
          color: #1a1a2e;
          margin-bottom: 0.25rem;
        }
        .inscription-title p {
          color: #6b7280;
          font-size: 0.95rem;
        }
        .step-nav-buttons {
          display: flex;
          justify-content: space-between;
          margin-top: 2rem;
          gap: 1rem;
        }
      `}</style>

      <div className="page-container" style={{ minHeight: '100vh', background: '#f8f9fa', padding: 0 }}>
        <header className="inscription-header">
          <div className="inscription-header-brand">
            <Music size={24} />
            <span>Harmonie</span>
          </div>
          <Link to="/login">
            <ArrowLeft size={16} />
            Retour à la connexion
          </Link>
        </header>

        <div className="inscription-body">
          <div className="inscription-title">
            <h1>Inscription à l'école de musique</h1>
            <p>Remplissez le formulaire ci-dessous pour vous inscrire</p>
          </div>

          <div className="form-stepper" style={{ display: 'flex', justifyContent: 'center', marginBottom: '2.5rem', gap: '0.5rem' }}>
            {STEPS.map((step, index) => {
              const StepIcon = step.icon;
              const isActive = index === currentStep;
              const isCompleted = index < currentStep;
              return (
                <div
                  key={index}
                  className={`stepper-step ${isActive ? 'active' : ''} ${isCompleted ? 'completed' : ''}`}
                  style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', opacity: isActive || isCompleted ? 1 : 0.45 }}
                >
                  <div className="stepper-icon" style={{
                    width: 40, height: 40, borderRadius: '50%',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    background: isActive ? 'linear-gradient(135deg, #7c3aed, #a855f7)' : isCompleted ? '#22c55e' : '#e5e7eb',
                    color: isActive || isCompleted ? '#fff' : '#9ca3af',
                  }}>
                    {isCompleted ? <Check size={18} /> : <StepIcon size={18} />}
                  </div>
                  <span className="stepper-label" style={{ fontSize: '0.8rem', fontWeight: isActive ? 600 : 400, color: isActive ? '#7c3aed' : '#6b7280', display: 'none' }}>
                    {step.label}
                  </span>
                  {index < STEPS.length - 1 && (
                    <ChevronRight size={16} style={{ color: '#d1d5db', margin: '0 0.25rem' }} />
                  )}
                </div>
              );
            })}
          </div>

          <div style={{ textAlign: 'center', marginBottom: '1.5rem', fontSize: '0.85rem', color: '#6b7280' }}>
            Étape {currentStep + 1} sur {STEPS.length} — <strong style={{ color: '#374151' }}>{STEPS[currentStep].label}</strong>
          </div>

          <div className="form-card" style={{ padding: '2rem', borderRadius: 16 }}>
            {renderStepContent()}

            <div className="step-nav-buttons">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setCurrentStep((s) => s - 1)}
                disabled={currentStep === 0}
                style={{ display: 'flex', alignItems: 'center', gap: 6, visibility: currentStep === 0 ? 'hidden' : 'visible' }}
              >
                <ArrowLeft size={16} /> Précédent
              </button>

              {currentStep < STEPS.length - 1 ? (
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => setCurrentStep((s) => s + 1)}
                  disabled={!canProceed()}
                  style={{ display: 'flex', alignItems: 'center', gap: 6 }}
                >
                  Suivant <ArrowRight size={16} />
                </button>
              ) : (
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleSubmit}
                  disabled={!canProceed()}
                  style={{ display: 'flex', alignItems: 'center', gap: 6 }}
                >
                  <CheckCircle size={16} /> Valider l'inscription
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default InscriptionPublique;
